import { useId, useRef, useState, type ReactNode } from "react";
import type { LeadVisibilityResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { RadioGroup } from "../../design/components/RadioGroup";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { leadVisibilityQuery, useSaveLeadVisibility } from "../../leads/lead-queries";
import { useAccess } from "../../session/access";
import { ConfirmChangesDialog, draftSummary, LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import "./lead-fields.css";

// Impostazioni → Chi vede i Lead (piano M2 Task 20, mock-up `regole.html?sezione=perimetro`; decisione del 28 settembre
// 2026): «Tutti i Lead dell'azienda» oppure «Soltanto i suoi» per lo User e i ruoli copiati da lui (livello 1); chi guida
// il team vede sempre tutti. La scelta riscrive «Lead → Vedere» e «Lead → Modificare» di quei ruoli (il server, Task 9).
// Quando i ruoli non sono d'accordo il server dice «misto» e nessuna delle due è segnata; un ruolo di livello 1 che non
// vede nessun Lead non si allarga, quindi dopo il salvataggio può restare «misto» (questione aperta 72). La vede ogni
// persona attiva; la cambia chi ha `settings.lead_visibility` e un ruolo sopra il livello 1 (`canEdit` del server), mai
// il Super admin di Gioia365 dall'interfaccia (come le altre regole dell'azienda, questione 43).

type Mode = "all" | "own";

export function LeadVisibilityPage() {
  return (
    <SettingsLayout section="leadVisibility">
      <LeadVisibilitySection />
    </SettingsLayout>
  );
}

function LeadVisibilitySection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const visibility = useQuery(leadVisibilityQuery(tab));
  const organization = access.context.organization.name;
  const allowed = access.can("settings.lead_visibility") && !access.isPlatform;
  const editable = allowed && visibility.data?.canEdit === true;

  let body: ReactNode;
  if (visibility.data) {
    body = <VisibilityEditor saved={visibility.data} editable={editable} />;
  } else if (visibility.isError) {
    body = (
      <div className="g-leadfields__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, visibility.error)}
        </Reason>
        <Button onClick={() => void visibility.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-leadfields__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("leadVisibility.about.title")}>
        <p>{t("leadVisibility.about.text", { organization })}</p>
        <h2>{t("leadVisibility.about.whyTitle")}</h2>
        <p>{t("leadVisibility.about.why")}</p>
      </AboutBox>
      {!allowed || (visibility.data && !visibility.data.canEdit) ? (
        <Reason>{access.isPlatform ? t("leadVisibility.readOnlyPlatform", { organization }) : t("leadVisibility.readOnly", { organization })}</Reason>
      ) : null}
      {body}
    </>
  );
}

function VisibilityEditor({ saved, editable }: { saved: LeadVisibilityResponse; editable: boolean }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const save = useSaveLeadVisibility(tab);
  const baseId = useId();
  const [choice, setChoice] = useState<Mode | null>(null);
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [notice, setNotice] = useState<Feedback | null>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);
  const saving = save.isPending;

  // Senza il permesso si vede il salvato; la scelta uguale a quella salvata non è una modifica.
  const draft = editable && choice !== null && choice !== saved.mode ? choice : null;
  const current = draft ?? (saved.mode === "mixed" ? null : saved.mode);
  const changes = draft ? [t(draft === "all" ? "leadVisibility.changes.all" : "leadVisibility.changes.own")] : [];
  const blocker = useLeaveGuard(draft !== null);
  const disabled = !editable || saving;

  async function apply() {
    if (!draft || savingNow.current) return;
    savingNow.current = true;
    setConfirming({ error: null });
    try {
      const response = await save.mutateAsync({ mode: draft });
      setConfirming(null);
      setChoice(null);
      if (response.mode === "mixed") {
        setNotice({ text: (tr) => tr("leadVisibility.stillMixed") });
      } else {
        setNotice(null);
        toast.show(t("leadVisibility.saved"), "success");
      }
    } catch (error) {
      setConfirming({ error: { text: (tr) => messageForError(tr, error) } });
    } finally {
      savingNow.current = false;
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
  }

  function leave() {
    setChoice(null);
    if (blocker.status === "blocked") blocker.proceed();
  }

  const titleId = `${baseId}-title`;
  const options: Array<{ mode: Mode; label: string }> = [
    { mode: "all", label: t("leadVisibility.all") },
    { mode: "own", label: t("leadVisibility.own") },
  ];

  return (
    <>
      {notice ? <Reason role="status">{notice.text(t)}</Reason> : null}
      <section className="g-leadfields__section" aria-labelledby={titleId}>
        <h2 id={titleId}>{t("leadVisibility.title")}</h2>
        <RadioGroup
          name={`${baseId}-mode`}
          legend={t("leadVisibility.legend")}
          hideLegend
          options={options.map((option) => ({ value: option.mode, label: option.label }))}
          value={current}
          disabled={disabled}
          onChange={(mode) => {
            setChoice(mode);
            setNotice(null);
          }}
        />
        <p>{t("leadVisibility.appliesTo")}</p>
        {saved.mode === "mixed" && !draft ? <Reason>{t("leadVisibility.mixed")}</Reason> : null}
        <p className="g-leadfields__muted">{t("leadVisibility.note")}</p>
      </section>

      {draft ? (
        <SaveBar
          summary={draftSummary(t, 1)}
          onDiscard={() => {
            setChoice(null);
            toast.show(t("settings.draft.discarded"));
          }}
          onSave={() => setConfirming({ error: null })}
          saveLabel={t("settings.draft.save")}
          saving={saving}
        />
      ) : null}

      {confirming && draft ? (
        <ConfirmChangesDialog changes={changes} error={confirming.error} saving={saving} onBack={() => setConfirming(null)} onApply={() => void apply()} />
      ) : null}

      {blocker.status === "blocked" ? <LeaveDialog onStay={stay} onLeave={leave} /> : null}
    </>
  );
}
